import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {
    SelectComboboxPopover,
    SelectItem,
    SelectList,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {SelectProvider} from '@ariakit/react'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useTranslation} from '@/translations/i18n.tsx'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useGetAccessProfiles} from '@/features/users/queries/useGetAccessProfiles'
import {Label} from '@/components/ui/label/Label'
import {Button} from '@/components/ui/button/Button'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {Role} from '@/features/users/types'

interface UserInputSelectAccessProfileProps {
    label: string
    value?: Omit<Role, 'permissions'> | null
    onChange: (value: Omit<Role, 'permissions'> | null) => void
    onClear?: () => void
    placeholder?: string
    errorMessage?: string
    isPortal?: boolean
}

export const UserInputSelectAccessProfile = ({
    label,
    placeholder,
    value,
    onChange,
    errorMessage,
    onClear,
    isPortal = true
}: UserInputSelectAccessProfileProps) => {
    const {t} = useTranslation()
    const accessProfilesQuery = useGetAccessProfiles({staleTime: Infinity})

    return (
        <SelectProvider
            value={value?.name ?? ''}
            setValue={value => onChange(accessProfilesQuery.data?.find(role => role.name == value) ?? null)}
        >
            <Flexbox direction="column" gap={1.5} fullWidth>
                <Label>{label}</Label>
                <SelectTrigger
                    placeholder={placeholder ?? t('commons:select')}
                    fullWidth
                    value={value?.name}
                    displayValue={() => (
                        <Flexbox fullWidth align="center" justify="space-between">
                            {value?.name}
                            {value && onClear && (
                                <Button variant="ghost" shape="text" onClick={onClear}>
                                    <XCloseIcon />
                                </Button>
                            )}
                        </Flexbox>
                    )}
                    hasError={!!errorMessage}
                />

                {errorMessage && (
                    <Flexbox align="center" gap={1.5}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                )}

                <SelectComboboxPopover portal={!!isPortal} sameWidth>
                    <SelectList>
                        {accessProfilesQuery.isLoading ? (
                            <>
                                {Array.from({length: 6}, (_, index) => (
                                    <Skeleton key={index} height={32} />
                                ))}
                            </>
                        ) : accessProfilesQuery.isError ? (
                            <InputHelpText error={t('errors:default')} />
                        ) : accessProfilesQuery.data && accessProfilesQuery.data.length > 0 ? (
                            <SelectVirtualList
                                data={accessProfilesQuery.data}
                                totalCount={accessProfilesQuery.data.length}
                                overscan={100}
                                itemContent={(itemIndex, item) => (
                                    <SelectItem hasItemCheckbox key={itemIndex} value={item.name}>
                                        {item.name}
                                    </SelectItem>
                                )}
                            />
                        ) : (
                            <InputHelpText helpText={t('commons:no_results')} />
                        )}
                    </SelectList>
                </SelectComboboxPopover>
            </Flexbox>
        </SelectProvider>
    )
}
